/**
 * Safely extract error message from API error response
 * @param {Error} error - The error object from API call
 * @param {string} fallbackMessage - Fallback message if no error details found
 * @returns {string} - Safe string error message
 */
export const extractErrorMessage = (error, fallbackMessage = 'An error occurred') => {
  if (!error) return fallbackMessage;

  // Try to get error message from response data
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // Ensure it's a string
    if (typeof detail === 'string') {
      return detail;
    }
    // If it's an object, try to extract meaningful information
    if (typeof detail === 'object') {
      if (detail.message) return detail.message;
      if (detail.error) return detail.error;
      // If it's a validation error object, try to format it
      if (Array.isArray(detail)) {
        return detail.map((err) => err.msg || err.message || 'Validation error').join(', ');
      }
    }
  }

  // Try to get error message from error object
  if (error.message && typeof error.message === 'string') {
    return error.message;
  }

  // Try to get error message from response status
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return 'Bad request';
      case 401:
        return 'Unauthorized';
      case 403:
        return 'Forbidden';
      case 404:
        return 'Not found';
      case 422:
        return 'Validation error';
      case 500:
        return 'Internal server error';
      default:
        return `HTTP ${error.response.status} error`;
    }
  }

  return fallbackMessage;
};
